/**
 * PropagationAuditPage.jsx
 *
 * Two-tab view:
 *  1. Sync Health Dashboard — per-DB cards showing last push time, row counts, status
 *  2. Propagation Audit Log — full paginated detail table
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Database, RefreshCw, CheckCircle, AlertTriangle, Clock,
  ArrowRight, Activity, Download, Filter, Search
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { API } from '../lib/api';

const getToken = () => sessionStorage.getItem('erp_security_token') || localStorage.getItem('erp_security_token');

const ACTION_COLOR = {
  MERGE_OK:    { bg:'rgba(34,197,94,.12)',  text:'#22c55e' },
  JUNCTION_OK: { bg:'rgba(59,130,246,.12)', text:'#3b82f6' },
  FAILED:      { bg:'rgba(239,68,68,.12)',  text:'#ef4444' },
};
const STATUS_COLOR = { SUCCESS:'#22c55e', FAILED:'#ef4444', PENDING:'#f59e0b' };

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', second:'2-digit'
  });
}
function timeAgo(iso) {
  if (!iso) return '—';
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60)   return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400)return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
}

/* ── Sync Health Dashboard ─────────────────────────────────────────────────── */
function SyncHealthDashboard({ onTrigger, onStats }) {
  const [health, setHealth] = useState([]);
  const [stats,  setStats]  = useState(null);
  const [loading,setLoading]= useState(true);
  const [pushing,setPushing]= useState(false);
  const [pushMsg,setPushMsg]= useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/uams/propagation-health`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (r.ok) { const d = await r.json(); setHealth(d.health||[]); setStats(d.stats||{}); if (onStats) onStats(d.stats||{}); }
    } catch (e) { console.warn('[PropagationAudit] Health fetch failed:', e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePush = async () => {
    setPushing(true); setPushMsg('Submitting…');
    try {
      const r = await fetch(`${API}/uams/propagate-to-all`, {
        method:'POST', headers:{ Authorization:`Bearer ${getToken()}`, 'Content-Type':'application/json' }
      });
      const d = await r.json();
      setPushMsg(d.accepted ? 'Propagation started in background — refresh in ~60s' : 'Failed to start');
    } catch { setPushMsg('Error — check server'); }
    finally { setPushing(false); }
    if (onTrigger) onTrigger();
  };

  if (loading) return <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)' }}>Loading health data…</div>;

  const healthy = health.filter(h => !h.LastFailedAt || new Date(h.LastSuccessAt) > new Date(h.LastFailedAt));
  const failing = health.filter(h => h.LastFailedAt  && (!h.LastSuccessAt || new Date(h.LastFailedAt) >= new Date(h.LastSuccessAt)));

  return (
    <div style={{ padding:'16px 20px', display:'flex', flexDirection:'column', gap:16 }}>

      {/* ── Overall stats banner ── */}
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
        {[
          { label:'Total DBs Tracked', val: stats?.totalDBs       || 0, color:'var(--accent-blue,#1890ff)', icon: Database },
          { label:'Healthy DBs',        val: stats?.healthyDBs     || 0, color:'#22c55e',  icon: CheckCircle  },
          { label:'DBs With Failures',  val: stats?.failedDBs      || 0, color:'#ef4444',  icon: AlertTriangle},
          { label:'Total Rows Affected',val: (stats?.totalRowsAffected||0).toLocaleString(), color:'#a855f7', icon: Activity },
        ].map(s => (
          <div key={s.label} style={{ display:'flex', alignItems:'center', gap:8,
            background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 14px', minWidth:150 }}>
            <s.icon size={16} color={s.color} />
            <div>
              <div style={{ fontSize:'1.1rem', fontWeight:700, color:'var(--text-primary)', lineHeight:1 }}>{s.val}</div>
              <div style={{ fontSize:'0.68rem', color:'var(--text-muted)' }}>{s.label}</div>
            </div>
          </div>
        ))}
        <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
          {pushMsg && <span style={{ fontSize:'0.78rem', color:'var(--text-secondary)' }}>{pushMsg}</span>}
          <Button size="sm" variant="outline" onClick={load} style={{ height:32 }}>
            <RefreshCw size={12} style={{ marginRight:4 }} /> Refresh
          </Button>
          <Button size="sm" onClick={handlePush} disabled={pushing} data-testid="sync-health-push-btn"
            style={{ height:32, background:'var(--accent-blue,#1890ff)', color:'#fff', border:'none' }}>
            <RefreshCw size={12} style={{ marginRight:4 }} />
            {pushing ? 'Starting…' : 'Sync All DBs Now'}
          </Button>
        </div>
      </div>

      {/* ── DB Health Grid ── */}
      {failing.length > 0 && (
        <div>
          <div style={{ fontSize:'0.78rem', fontWeight:700, color:'#ef4444', marginBottom:8, textTransform:'uppercase', letterSpacing:'.06em' }}>
            DBs with recent failures
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:10 }}>
            {failing.map(h => <DBCard key={h.TargetDB} h={h} status="FAILED" />)}
          </div>
        </div>
      )}

      <div>
        <div style={{ fontSize:'0.78rem', fontWeight:700, color:'var(--text-muted)', marginBottom:8, textTransform:'uppercase', letterSpacing:'.06em' }}>
          All tracked databases ({health.length})
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:10 }}>
          {health.map(h => {
            const ok = !h.LastFailedAt || new Date(h.LastSuccessAt||0) > new Date(h.LastFailedAt||0);
            return <DBCard key={h.TargetDB} h={h} status={ok ? 'SUCCESS' : 'FAILED'} />;
          })}
        </div>
        {health.length === 0 && (
          <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)', fontSize:'.85rem' }}>
            No propagation history yet. Click "Sync All DBs Now" to start.
          </div>
        )}
      </div>
    </div>
  );
}

function DBCard({ h, status }) {
  const borderColor = status === 'FAILED' ? '#ef4444' : '#22c55e';
  return (
    <div style={{ background:'var(--surface)', border:`1px solid var(--border)`, borderTop:`3px solid ${borderColor}`,
      borderRadius:8, padding:'12px 14px', display:'flex', flexDirection:'column', gap:6 }}>
      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
        <Database size={13} color={borderColor} />
        <span style={{ fontWeight:700, fontSize:'.82rem', color:'var(--text-primary)', flex:1,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{h.TargetDB}</span>
        <span style={{ fontSize:'.68rem', padding:'2px 7px', borderRadius:10, fontWeight:700,
          background: status==='FAILED' ? 'rgba(239,68,68,.12)' : 'rgba(34,197,94,.12)',
          color: borderColor }}>
          {status}
        </span>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'3px 10px', fontSize:'.75rem', color:'var(--text-secondary)' }}>
        <span style={{ color:'var(--text-muted)' }}>Last push</span>
        <span style={{ color:'var(--text-primary)', fontWeight:600 }}>{timeAgo(h.LastPushAt)}</span>
        <span style={{ color:'var(--text-muted)' }}>Inserted</span><span>+{h.TotalInserted||0}</span>
        <span style={{ color:'var(--text-muted)' }}>Updated</span> <span>~{h.TotalUpdated||0}</span>
        <span style={{ color:'var(--text-muted)' }}>Deactivated</span><span>-{h.TotalDeactivated||0}</span>
        <span style={{ color:'var(--text-muted)' }}>Total runs</span><span>{h.TotalRuns||0}</span>
        {h.FailureCount > 0 && <>
          <span style={{ color:'#ef4444' }}>Failures</span>
          <span style={{ color:'#ef4444', fontWeight:600 }}>{h.FailureCount}</span>
        </>}
        {h.LastGeo && <>
          <span style={{ color:'var(--text-muted)' }}>Last geo</span>
          <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{h.LastGeo}</span>
        </>}
      </div>
      <div style={{ fontSize:'.69rem', color:'var(--text-muted)', marginTop:2 }}>
        {fmtTime(h.LastPushAt)}
      </div>
    </div>
  );
}

/* ── Detailed Audit Log ──────────────────────────────────────────────────────── */
function PropagationAuditLog() {
  const [rows,       setRows]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [statusFlt,  setStatusFlt]  = useState('');
  const [clearing,   setClearing]   = useState(false);   // 'idle'|'confirm'|'doing'
  const PAGE_SIZE = 50;

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, pageSize: PAGE_SIZE });
      if (search)    params.set('targetDB', search);
      if (statusFlt) params.set('status',   statusFlt);
      const r = await fetch(`${API}/uams/propagation-log?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (r.ok) {
        const d = await r.json();
        setRows(d.rows||[]); setTotal(d.total||0); setTotalPages(d.totalPages||1); setPage(p);
      }
    } catch (e) { console.error('[PropagationAudit] Log fetch failed:', e.message); }
    finally { setLoading(false); }
  }, [search, statusFlt]);

  useEffect(() => { load(1); }, [load]);

  const exportCSV = () => {
    const hdr = ['LogId','PropId','TargetDB','TableName','Action','RowsInserted','RowsUpdated','RowsDeactivated','Actor','ActorIP','ActorGeo','StartedAt','CompletedAt','DurationMs','Status','ErrorMsg'];
    const csv = [hdr.join(','), ...rows.map(r =>
      hdr.map(k => `"${(r[k]??'').toString().replace(/"/g,'""')}"`).join(',')
    )].join('\n');
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'})); a.download=`prop_audit_${Date.now()}.csv`; a.click();
  };

  const handleTruncate = async () => {
    setClearing('doing');
    try {
      const r = await fetch(`${API}/uams/propagation-log/truncate`, {
        method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }
      });
      const d = await r.json();
      if (r.ok) { load(1); }
      else { alert(d.error || 'Truncate failed'); }
    } catch (e) { alert(e.message); }
    setClearing(false);
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 320px)', minHeight:380 }}>
      {/* Toolbar */}
      <div style={{ display:'flex', gap:8, padding:'10px 20px', borderBottom:'1px solid var(--border)', alignItems:'center', flexWrap:'wrap' }}>
        <div style={{ position:'relative', flex:'0 0 220px' }}>
          <Search size={12} style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)' }} />
          <Input placeholder="Filter by target DB…" value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key==='Enter' && load(1)}
            style={{ paddingLeft:26, height:32, fontSize:'.8rem' }} />
        </div>
        <select value={statusFlt} onChange={e => setStatusFlt(e.target.value)}
          style={{ height:32, fontSize:'.8rem', border:'1px solid var(--border)', borderRadius:6,
            background:'var(--surface)', color:'var(--text-primary)', padding:'0 8px' }}>
          <option value="">All Status</option>
          <option value="SUCCESS">SUCCESS</option>
          <option value="FAILED">FAILED</option>
        </select>
        <Button size="sm" onClick={() => load(1)} style={{ height:32 }}>
          <Filter size={12} style={{ marginRight:4 }} /> Apply
        </Button>
        <Button size="sm" variant="ghost" onClick={() => { setSearch(''); setStatusFlt(''); }} style={{ height:32 }}>Clear</Button>
        <span style={{ flex:1 }} />
        <span style={{ fontSize:'.75rem', color:'var(--text-muted)' }}>{total.toLocaleString()} entries</span>
        <Button size="sm" variant="outline" onClick={exportCSV} style={{ height:32 }}>
          <Download size={12} style={{ marginRight:4 }} /> CSV
        </Button>
        <Button size="sm" variant="outline" onClick={() => load(page)} style={{ height:32 }}>
          <RefreshCw size={12} />
        </Button>
        {/* Clear Log */}
        {clearing === 'confirm' ? (
          <>
            <span style={{ fontSize:'.75rem', color:'#ef4444', fontWeight:600 }}>Truncate all rows?</span>
            <Button size="sm" onClick={handleTruncate}
              style={{ height:32, background:'#ef4444', color:'#fff', border:'none', fontWeight:700 }}>
              Yes, Clear
            </Button>
            <Button size="sm" variant="outline" onClick={() => setClearing(false)} style={{ height:32 }}>Cancel</Button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setClearing('confirm')}
            disabled={clearing === 'doing'}
            style={{ height:32, color:'#ef4444', borderColor:'#ef4444' }}
            data-testid="prop-log-clear-btn">
            {clearing === 'doing' ? 'Clearing…' : 'Clear Log'}
          </Button>
        )}
      </div>

      {/* Table */}
      <div style={{ flex:1, overflow:'auto' }}>
        {loading ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)', fontSize:'.85rem' }}>Loading…</div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'.78rem' }}>
            <thead>
              <tr style={{ background:'var(--surface-hover)', position:'sticky', top:0, zIndex:5 }}>
                {['Time','PropId','Target DB','Table','Action','+Ins','~Upd','-Deact','Actor','IP / Geo','ms','Status'].map(h => (
                  <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontWeight:600,
                    color:'var(--text-muted)', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={12} style={{ padding:32, textAlign:'center', color:'var(--text-muted)' }}>No entries found.</td></tr>
              ) : rows.map(r => {
                const ac = ACTION_COLOR[r.Action] || { bg:'rgba(100,100,100,.1)', text:'var(--text-muted)' };
                return (
                  <tr key={r.LogId} style={{ borderBottom:'1px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.background='var(--surface-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background=''}>
                    <td style={{ padding:'6px 10px', whiteSpace:'nowrap', color:'var(--text-muted)', fontSize:'.72rem' }}>{fmtTime(r.StartedAt)}</td>
                    <td style={{ padding:'6px 10px', fontSize:'.7rem', color:'var(--text-muted)', fontFamily:'monospace' }}>{(r.PropId||'').slice(0,8)}…</td>
                    <td style={{ padding:'6px 10px', fontWeight:600, color:'var(--text-primary)', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.TargetDB}</td>
                    <td style={{ padding:'6px 10px', color:'var(--text-secondary)', fontSize:'.73rem', whiteSpace:'nowrap' }}>{r.TableName||'—'}</td>
                    <td style={{ padding:'6px 10px' }}>
                      <span style={{ padding:'2px 7px', borderRadius:6, fontSize:'.68rem', fontWeight:700, background:ac.bg, color:ac.text }}>{r.Action}</span>
                    </td>
                    <td style={{ padding:'6px 10px', color:'#22c55e', textAlign:'right' }}>{r.RowsInserted||0}</td>
                    <td style={{ padding:'6px 10px', color:'#3b82f6', textAlign:'right' }}>{r.RowsUpdated||0}</td>
                    <td style={{ padding:'6px 10px', color:'#f59e0b', textAlign:'right' }}>{r.RowsDeactivated||0}</td>
                    <td style={{ padding:'6px 10px', color:'var(--text-secondary)' }}>{r.Actor||'—'}</td>
                    <td style={{ padding:'6px 10px', fontSize:'.7rem', color:'var(--text-muted)', maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.ActorGeo||''}>
                      {r.ActorIP||'—'}{r.ActorGeo ? ` · ${r.ActorGeo}` : ''}
                    </td>
                    <td style={{ padding:'6px 10px', color:'var(--text-muted)', textAlign:'right' }}>{r.DurationMs||'—'}</td>
                    <td style={{ padding:'6px 10px' }}>
                      <span style={{ fontSize:'.7rem', fontWeight:700, color: STATUS_COLOR[r.Status]||'var(--text-muted)' }}>{r.Status}</span>
                      {r.ErrorMsg && <span title={r.ErrorMsg} style={{ marginLeft:4, cursor:'help' }}><AlertTriangle size={11} color="#ef4444" /></span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'7px 20px', borderTop:'1px solid var(--border)', background:'var(--surface-hover)' }}>
          <span style={{ fontSize:'.75rem', color:'var(--text-muted)' }}>Page {page}/{totalPages}</span>
          <div style={{ display:'flex', gap:5 }}>
            <Button size="sm" variant="outline" disabled={page<=1} onClick={()=>load(page-1)} style={{ height:26, fontSize:'.75rem' }}>Prev</Button>
            {Array.from({ length:Math.min(5,totalPages) },(_,i)=>{ const start=Math.max(1,Math.min(page-2,totalPages-4)); const p=start+i;
              return <Button key={p} size="sm" variant={p===page?'default':'outline'} onClick={()=>load(p)} style={{ height:26, minWidth:26, fontSize:'.75rem', padding:'0 7px' }}>{p}</Button>;
            })}
            <Button size="sm" variant="outline" disabled={page>=totalPages} onClick={()=>load(page+1)} style={{ height:26, fontSize:'.75rem' }}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main page ──────────────────────────────────────────────────────────────── */
export default function PropagationAuditPage() {
  const [tab, setTab] = useState('health');
  const [stats, setStats] = useState(null);

  const TABS = [
    { id:'health', label:'Sync Health Dashboard', icon: Activity },
    { id:'log',    label:'Propagation Audit Log', icon: Clock    },
  ];

  return (
    <div data-testid="propagation-audit-page"
      style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 200px)', minHeight:600 }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 20px 0', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
        <Database size={18} color="var(--accent-blue,#1890ff)" />
        <span style={{ fontWeight:700, fontSize:'1.05rem', color:'var(--text-primary)' }}>DB Propagation Centre</span>
        <span style={{ fontSize:'.72rem', color:'var(--text-muted)', background:'var(--surface-hover)', padding:'2px 8px', borderRadius:20 }}>
          {stats
            ? `MasterDB → ${stats.templateCount ?? 0} Template DB${(stats.templateCount??0)!==1?'s':''} + ${stats.clientCount ?? 0} Client DB${(stats.clientCount??0)!==1?'s':''}`
            : 'MasterDB → All Template & Client DBs'}
        </span>
        <div style={{ marginLeft:'auto', display:'flex', gap:0, borderBottom:'2px solid transparent' }}>
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                data-testid={`prop-tab-${t.id}`}
                style={{ display:'flex', alignItems:'center', gap:5, padding:'8px 16px', border:'none', cursor:'pointer',
                  background:'none', borderBottom:`3px solid ${active?'var(--accent-blue,#1890ff)':'transparent'}`,
                  color: active?'var(--accent-blue,#1890ff)':'var(--text-muted)',
                  fontWeight: active ? 700 : 500, fontSize:'.86rem', marginBottom:-1 }}>
                <Icon size={13} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex:1, overflow:'auto' }}>
        {tab === 'health' && <SyncHealthDashboard onTrigger={() => {}} onStats={setStats} />}
        {tab === 'log'    && <PropagationAuditLog />}
      </div>
    </div>
  );
}
